// Sentry error monitoring — initialised before React mounts.
//
// We only init Sentry when VITE_SENTRY_DSN is set, so local dev without
// the env var stays silent (no console noise, no failed network calls
// to the Sentry ingest endpoint). Production + Vercel preview both
// have the DSN set and report normally.
//
// `environment` is derived from Vite's MODE so production errors and
// preview-deploy errors land in the right Sentry environment buckets.
//
// `release` uses Vercel's commit SHA when available, falling back to
// 'dev' locally. This makes "what version saw this error" answerable
// without us shipping a manual version string in package.json.

import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN
const ENV = import.meta.env.MODE
const RELEASE =
  import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.slice(0, 7) ?? 'dev'

let initialized = false

export function initSentry() {
  if (initialized) return
  if (!DSN) {
    // Quiet in dev. Don't even import the heavier integrations.
    return
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,

    // Performance traces — keep low to stay inside Sentry's free
    // monthly quota. We can crank this up later if we actually need
    // perf data.
    tracesSampleRate: 0.1,

    // Don't auto-record sessions or replays; those eat the free
    // event quota fast and we don't need them at this stage.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Strip noise: browser extensions, ResizeObserver loop spam,
    // network blips that aren't actionable.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Common third-party / extension noise
      'Non-Error promise rejection captured',
      // Network timeouts that aren't our bug
      'NetworkError when attempting to fetch resource',
      'Failed to fetch',
    ],

    // Don't send PII automatically. We attach a user object explicitly
    // after auth so we know it's only the email we care about.
    sendDefaultPii: false,
  })

  initialized = true
}

// Attach the signed-in user's email to subsequent events so we can
// answer "which user hit this?" from the Sentry dashboard. Call this
// from useSession when the auth state resolves.
export function setSentryUser(user) {
  if (!initialized) return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.id,
    email: user.email ?? undefined,
  })
}

// Re-export the bits the rest of the app might need without forcing
// every caller to take a direct dependency on @sentry/react.
export const ErrorBoundary = Sentry.ErrorBoundary
export const captureException = Sentry.captureException
